import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import withPage from '../withPage';
import { AppDispatch, RootState } from '@/redux/store';
import { useEffect } from 'react';
import { doGetCarrier } from '../../../modules/carriers/stores/thunk';
import { useSelector } from 'react-redux';
import { CarrierWithIntegrations } from './carrier.types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Building2, Settings, Bike, Network, Map, Code, Camera } from 'lucide-react';
import moment from 'moment';
import { CarrierLogoModal } from './CarrierLogoModal';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../ui/breadcrumb';
import { CarrierPageSkeleton } from './CarrierPageSkeleton';
import { clearCarrierState } from '../../../modules/carriers/stores/slice';
import { CarrierHubsSection } from './CarrierHubsSection';

const CarrierPage = ({ id }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [logoModalOpen, setLogoModalOpen] = useState(false);

  // Fix: Use the correct selector for single carrier
  const carrier: CarrierWithIntegrations = useSelector((state: RootState) => state.carriers.carrier);

  useEffect(() => {
    if (id) {
      dispatch(doGetCarrier({ id: parseInt(id) }));
    }
    // Cleanup function to clear carrier state when component unmounts
    return () => {
      dispatch(clearCarrierState());
    };
  }, [id, dispatch]);

  if (!carrier) {
    return <CarrierPageSkeleton />;
  }

  const formatDate = (dateString: string | Date) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const getAreaTypeIcon = (type: string) => {
    return type === 'AREA' ? <Map className="h-4 w-4" /> : <Code className="h-4 w-4" />;
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/carriers" className="text-xl font-bold text-black">
              Carriers
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-m font-bold text-black-600">{carrier.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center space-x-4 min-w-[1200px]">
        <div
          className="h-16 w-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden relative group cursor-pointer hover:bg-muted/80 transition-colors"
          onClick={() => setLogoModalOpen(true)}
        >
          {carrier.logo ? (
            <img
              src={carrier.logo}
              alt={carrier.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          <Building2 className="h-8 w-8 text-muted-foreground" />

          {/* Overlay with camera icon */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Camera className="h-6 w-6 text-white" />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{carrier.name}</h1>
          <p className="text-muted-foreground">Carrier ID: {carrier.id}</p>
        </div>
      </div>

      {/* General Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            General Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Carrier Name</p>
              <p className="text-sm">{carrier.carrierName || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Created</p>
              <p className="text-sm">{formatDate(carrier.createdAt)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
              <p className="text-sm">{formatDate(carrier.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Carrier Integrations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Network className="h-5 w-5" />
            Carrier Integrations ({carrier.carrierIntegrations?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Manage and view carrier integrations and their associated delivery areas.
          </p>

          {carrier.carrierIntegrations && carrier.carrierIntegrations.length > 0 ? (
            <div className="space-y-4">
              {carrier.carrierIntegrations.map((integration) => (
                <Card key={integration.id}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="font-medium text-lg truncate">{integration.name}</span>
                        <Badge
                          variant="outline"
                          className="flex-shrink-0"
                          style={{
                            backgroundColor: integration.sortingColor,
                            color: '#fff',
                            borderColor: integration.sortingColor,
                          }}
                        >
                          {integration.sortingName}
                        </Badge>
                      </div>
                      <Badge variant="secondary" className="flex-shrink-0">
                        {integration.deliveryArea?.length || 0} delivery areas
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {integration.deliveryArea && integration.deliveryArea.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {integration.deliveryArea.map((area) => (
                          <div
                            key={area.id}
                            className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors min-w-0"
                          >
                            {getAreaTypeIcon(area.type)}
                            <span className="text-sm font-medium truncate">{area.name}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 text-muted-foreground">
                        <Map className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm">No delivery areas configured for this integration</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Network className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No integrations found</h3>
              <p className="text-muted-foreground">This carrier has no integrations configured</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Carrier Hubs */}
      <CarrierHubsSection
        carrierId={carrier.id}
        hubs={carrier.carrierHubs} // This will be populated from the backend later
      />

      {/* Logo Upload Modal */}
      <CarrierLogoModal
        open={logoModalOpen}
        onOpenChange={setLogoModalOpen}
        carrierId={carrier.id}
        currentLogo={carrier.logo}
        carrierName={carrier.name}
      />
    </div>
  );
};

export default withPage(CarrierPage);
